import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { doc, setDoc } from 'firebase/firestore';
import { db } from '../services/firebase';
import { findUserByUsername } from '../services/userService';

// Higher quality cricket stadium image from Unsplash
const cricketImage = "https://images.unsplash.com/photo-1531415074968-036ba1b575da?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1167&q=80";

const AuthPage = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isOver18, setIsOver18] = useState(false);
  const [loginMethod, setLoginMethod] = useState('email'); // 'email' or 'username'
  
  const { login, signup } = useAuth();
  const navigate = useNavigate();
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (isLogin) {
      if (loginMethod === 'email' && (!email || !password)) {
        setError('Please enter both email and password');
        return;
      }
      
      if (loginMethod === 'username' && (!username || !password)) {
        setError('Please enter both username and password');
        return;
      }
    } else {
      // Registration validation
      if (!email || !password || !confirmPassword || !username) {
        setError('Please fill in all required fields');
        return;
      }
      
      if (password !== confirmPassword) {
        setError('Passwords do not match');
        return;
      }
      
      if (username.length < 3) {
        setError('Username should be at least 3 characters');
        return;
      }
      
      if (!isOver18) {
        setError('You must confirm that you are at least 18 years old');
        return;
      }
    }
    
    try {
      setLoading(true);
      if (isLogin) {
        if (loginMethod === 'email') {
          await login(email, password);
        } else {
          // Find user by username
          const user = await findUserByUsername(username);
          
          if (!user) {
            throw new Error('Username not found');
          }
          
          // Login with email and password using the found email
          await login(user.email, password);
        }
      } else {
        // Register new user
        const result = await signup(email, password);
        
        // Create user profile with username
        if (result.user) {
          const userRef = doc(db, 'users', result.user.uid);
          await setDoc(userRef, {
            email: result.user.email,
            username: username,
            displayName: username,
            createdAt: new Date(),
            lastLogin: new Date(),
            role: 'user'
          }, { merge: true });
        }
      }
      navigate('/');
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-900 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl overflow-hidden w-full max-w-4xl flex flex-col md:flex-row">
        {/* Left Side - Image & Info */}
        <div className="relative w-full md:w-1/2 bg-blue-600 p-8 md:p-12 text-white flex flex-col justify-center">
          <div className="absolute inset-0 opacity-30 z-0" style={{ backgroundImage: `url(${cricketImage})`, backgroundSize: 'cover', backgroundPosition: 'center' }}></div>
          <div className="relative z-10">
            <h2 className="text-3xl font-bold mb-6">GamePreBattle</h2>
            <p className="text-xl mb-8">The Premier Cricket Prediction Platform</p>
            <div className="space-y-4 mb-8">
              <div className="flex items-center">
                <svg className="h-5 w-5 mr-3" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span>Predict match outcomes</span>
              </div>
              <div className="flex items-center">
                <svg className="h-5 w-5 mr-3" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span>Win rewards for accuracy</span>
              </div>
              <div className="flex items-center">
                <svg className="h-5 w-5 mr-3" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span>Join a community of cricket fans</span>
              </div>
            </div>
            <div className="bg-white/20 p-4 rounded-lg text-sm">
              <p>⚠️ Age verification required</p>
              <p>You must be 18+ to use GamePreBattle</p>
            </div>
          </div>
        </div>
        
        {/* Right Side - Form */}
        <div className="w-full md:w-1/2 p-8 md:p-12 flex items-center justify-center">
          <div className="max-w-md w-full">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-gray-900">{isLogin ? 'Login' : 'Create Account'}</h2>
              <p className="text-gray-600 mt-2">
                {isLogin ? 'Welcome back to GamePreBattle!' : 'Start your cricket prediction journey'}
              </p>
            </div>
            
            {error && (
              <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-6 rounded">
                <p className="font-medium">Error</p>
                <p className="text-sm">{error}</p>
              </div>
            )}
            
            <form onSubmit={handleSubmit} className="space-y-6">
              {isLogin && (
                <div className="flex justify-center space-x-4 mb-4">
                  <button
                    type="button"
                    className={`px-4 py-2 rounded-md ${loginMethod === 'email' ? 'bg-blue-600 text-white' : 'bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200'}`}
                    onClick={() => setLoginMethod('email')}
                  >
                    Email
                  </button>
                  <button
                    type="button"
                    className={`px-4 py-2 rounded-md ${loginMethod === 'username' ? 'bg-blue-600 text-white' : 'bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200'}`}
                    onClick={() => setLoginMethod('username')}
                  >
                    Username
                  </button>
                </div>
              )}
              
              {(!isLogin || (isLogin && loginMethod === 'email')) && (
                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-2" htmlFor="email">
                    Email Address
                  </label>
                  <input
                    type="email"
                    id="email"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="your@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required={!isLogin || (isLogin && loginMethod === 'email')}
                  />
                </div>
              )}
              
              {(!isLogin || (isLogin && loginMethod === 'username')) && (
                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-2" htmlFor="username">
                    Username
                  </label>
                  <input
                    type="text"
                    id="username"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Your username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    required={!isLogin || (isLogin && loginMethod === 'username')}
                  />
                  {!isLogin && (
                    <p className="text-xs text-gray-500 mt-1">
                      Username must be at least 3 characters
                    </p>
                  )}
                </div>
              )}
              
              <div>
                <label className="block text-gray-700 text-sm font-medium mb-2" htmlFor="password">
                  Password
                </label>
                <input
                  type="password"
                  id="password"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>
              
              {!isLogin && (
                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-2" htmlFor="confirmPassword">
                    Confirm Password
                  </label>
                  <input
                    type="password"
                    id="confirmPassword"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required={!isLogin}
                  />
                </div>
              )}
              
              {!isLogin && (
                <div className="flex items-center">
                  <input
                    id="age-check"
                    type="checkbox"
                    className="h-4 w-4 text-indigo-600 border-gray-300 rounded"
                    checked={isOver18}
                    onChange={(e) => setIsOver18(e.target.checked)}
                    required
                  />
                  <label htmlFor="age-check" className="ml-2 block text-sm text-gray-700">
                    I confirm that I am at least 18 years old
                  </label>
                </div>
              )}
              
              <button
                type="submit"
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-4 rounded-lg transition duration-150 ease-in-out"
                disabled={loading}
              >
                {loading ? 'Please wait...' : isLogin ? 'Sign In' : 'Create Account'}
              </button>
              
              <div className="text-center mt-4">
                <button
                  type="button"
                  className="text-indigo-600 hover:text-indigo-800 text-sm font-medium"
                  onClick={() => setIsLogin(!isLogin)}
                >
                  {isLogin ? 'Need an account? Sign up' : 'Already have an account? Sign in'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthPage; 